/**
 * SignalChart — price + indicator panes for one screener signal.
 *
 * Draws the indicators the screener actually used, with the screener's own
 * per-bar verdict, so a signal can be read off the chart rather than taken on
 * trust. Values come from the backend replay of the same attribute code the
 * screener ran; this component only lays them out.
 *
 * Props:
 *   ohlcv        [{date, open, high, low, close, volume}]
 *   series       typed series from /screeners/:id/indicator-series
 *   evaluations  [{date, passed, score, conditions, values}]
 *   blockers     {date: [attribute names]}
 *   signalDate   'YYYY-MM-DD' — the bar the user selected
 *   warnings     strings surfaced above the chart
 *   deps         whether dependency series were requested
 *   onToggleDeps callback for the dependency switch
 */
import { useEffect, useMemo, useState } from 'react';

import ChartPane from './ChartPane';
import IndicatorLegend from './IndicatorLegend';
import MatchPanel from './MatchPanel';
import {
  PANE_PRICE,
  indexEvaluations,
  prepareChart,
} from '../../lib/signalSeries';

export default function SignalChart({
  ohlcv = [],
  series = [],
  evaluations = [],
  blockers = {},
  signalDate = null,
  warnings = [],
  error = null,
  deps = false,
  onToggleDeps,
  loading = false,
}) {
  const {
    series: decorated,
    panes,
    rows,
    visible: initialVisible,
  } = useMemo(() => prepareChart({ series, ohlcv }), [series, ohlcv]);

  const evalByDate = useMemo(
    () => indexEvaluations(evaluations),
    [evaluations]
  );

  const [visible, setVisible] = useState(initialVisible);
  const [hovered, setHovered] = useState(null);

  // Reset to defaults when the series set itself changes (new symbol, or
  // dependencies toggled), but keep the user's choices within one series set.
  const seriesKey = useMemo(
    () => decorated.map((s) => s.id).join('|'),
    [decorated]
  );
  useEffect(() => {
    setVisible(initialVisible);
    setHovered(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesKey]);

  const toggle = (id) =>
    setVisible((prev) => ({ ...prev, [id]: !(prev[id] !== false) }));
  const toggleMany = (ids, on) =>
    setVisible((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = on; });
      return next;
    });

  // Windows where a signal attribute was active, shaded on the price pane so
  // "the cross was on from here to here" is visible without hovering.
  //
  // Only `event` series are shaded. Boolean state flags are read far better as
  // checklist rows — a screener like gmma_trend has seven of them, and seven
  // overlapping translucent bands would just fog the price pane.
  const annotationSpans = useMemo(() => {
    const out = [];
    for (const s of decorated) {
      if (visible[s.id] === false || s.pane !== 'event') continue;
      for (const span of s.spans || []) {
        out.push({ id: s.id, from: span.from, to: span.to, color: s.color });
      }
    }
    return out;
  }, [decorated, visible]);

  const matchDots = useMemo(() => {
    const closeByDate = new Map(ohlcv.map((b) => [b.date, b.close]));
    return evaluations
      .filter((e) => e.passed && closeByDate.has(e.date))
      .map((e) => ({ date: e.date, close: closeByDate.get(e.date) }));
  }, [evaluations, ohlcv]);

  const focusDate =
    hovered || signalDate || (rows.length ? rows[rows.length - 1].date : null);
  const focusRow = rows.find((r) => r.date === focusDate);
  const lastValuePane = panes.length - 1;

  if (!ohlcv.length) {
    return (
      <div className="flex h-full items-center justify-center font-mono text-sm text-muted-foreground">
        [ No chart data available ]
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col gap-3 transition-opacity ${
        loading ? 'opacity-50' : ''
      }`}
    >
      {error && (
        <p className="rounded-md border border-loss/30 bg-loss/5 px-3 py-2 text-xs text-loss">
          {error} — showing price only.
        </p>
      )}

      {warnings.length > 0 && (
        <ul className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] leading-snug text-amber-600 dark:text-amber-400">
          {warnings.map((warning) => (
            <li key={warning} className="font-mono">{warning}</li>
          ))}
        </ul>
      )}

      <MatchPanel
        evaluation={evalByDate[focusDate]}
        date={focusDate}
        blockers={blockers[focusDate]}
        isSignalBar={focusDate === signalDate}
      />

      <div className="space-y-1" onMouseLeave={() => setHovered(null)}>
        {panes.map((pane, i) => (
          <ChartPane
            key={pane.id}
            pane={pane}
            rows={rows}
            allSeries={decorated}
            visible={visible}
            signalDate={signalDate}
            evaluations={evalByDate}
            blockers={blockers}
            showXAxis={i === lastValuePane}
            annotations={pane.kind === PANE_PRICE ? annotationSpans : []}
            matchDots={pane.kind === PANE_PRICE ? matchDots : []}
            onActiveChange={setHovered}
          />
        ))}
      </div>

      <IndicatorLegend
        series={decorated}
        visible={visible}
        onToggle={toggle}
        onToggleMany={toggleMany}
        row={focusRow}
      />

      {onToggleDeps && (
        <label className="flex cursor-pointer items-center gap-2 px-1.5 text-[11px] text-muted-foreground">
          <input
            type="checkbox"
            checked={deps}
            onChange={(e) => onToggleDeps(e.target.checked)}
            className="h-3 w-3 accent-[hsl(var(--primary))]"
          />
          Fetch dependency indicators (ribbon EMAs, intermediate averages)
        </label>
      )}
    </div>
  );
}
